import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { AnyAction } from 'redux';
import {
  call,
  cancel,
  delay,
  fork,
  join,
  put,
  race,
  select,
  take,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  GlobalActions,
  SOFORTSCHUTZ_AVAILABLE_REQUEST,
  ZAEHLSTUECKEREQUEST,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { childKeyOf } from '../models/childKeys';
import { areAllLinienPriced } from '../models/angebotAdoption';
import { TiergruppeState } from '../models/tiergruppe.model';
import { TakeOverAgainPayload, TgvAction, TgvActions } from '../tgvActions';
import {
  hasAbortedOffers,
  isNavigationToDashboard,
  removeAbortedOffers,
  restoreRepeatedRun,
} from './groupFailureSaga';
import { findOrphanKeys, removeOrphanedOffersSaga } from './orphanedOffersSaga';
import { readTakeOverOffers, TgvTakeOverOffer } from './angebotOffersReader';
import { SHOP_RUN_WAIT_MS } from './pendingDeletionWait';
import { dispatchToStoreOf, findStoreState, sharedDispatcher } from './sharedDispatcher';
import { snapshotManager } from './streckeSnapshotSaga';

/**
 * The action every child app handles itself when its own offer page continues to the dashboard
 * (TkvActions.CONTINUE_TO_DASHBOARD, ThhHundActions / ThhPferdActions.ABSPRUNG_INS_DASHBOARD).
 * The apps must not import each other. ABSPRUNG_INS_DASHBOARD is shared by nine product apps, so
 * it is only ever sent to the store of the offer (see dispatchToStoreOf).
 */
export const INS_DASHBOARD_ACTION: Record<string, string> = {
  [App.TKV_V2]: 'CONTINUE_TO_DASHBOARD',
  [App.THH_HUND]: 'ABSPRUNG_INS_DASHBOARD',
  [App.THH_PFERD]: 'ABSPRUNG_INS_DASHBOARD',
};

export function* watchInsDashboard(): SagaIterator {
  yield takeLeading(TgvActions.TGV_INS_DASHBOARD_CLICKED, safeSaga(insDashboardSaga, App.TGV));
  yield takeLatest(
    TgvActions.TGV_TAKE_OVER_AGAIN_REQUESTED,
    safeSaga(takeRequestedOffersOverAgain, App.TGV),
  );
}

export const UEBERNAHME_TIMEOUT_MS = 120000;

/** Time in which the browser reports the change of the address after a navigation. */
export const NAVIGATION_SETTLE_MS = 300;

const SHOP_UPDATE_FAILED_EVENTS: string[] = [
  SOFORTSCHUTZ_AVAILABLE_REQUEST.SOFORTSCHUTZ_AVAILABLE_REQUEST_FAILED,
  ZAEHLSTUECKEREQUEST.ZAEHLSTUECKE_REQUEST_FAILED,
];

enum UebernahmeResult {
  DONE = 'DONE',
  FAILED = 'FAILED',
  FAILED_AFTER_LEAVING = 'FAILED_AFTER_LEAVING',
  NOT_FINISHED = 'NOT_FINISHED',
  CANCELLED = 'CANCELLED',
  ABANDONED = 'ABANDONED',
}

const isTakeOverOf =
  (offer: Pick<TgvTakeOverOffer, 'app' | 'angebotsId'>) =>
  (action: AnyAction): boolean =>
    action.type === GlobalActions.IN_DASHBOARD_UEBERNEHMEN &&
    action.payload?.appId === offer.app &&
    action.payload?.angebotsId === offer.angebotsId &&
    !action.payload?.deferTarifierung;

const endsOnDashboard: Record<UebernahmeResult, boolean> = {
  [UebernahmeResult.DONE]: true,
  [UebernahmeResult.FAILED]: false,
  [UebernahmeResult.FAILED_AFTER_LEAVING]: true,
  [UebernahmeResult.NOT_FINISHED]: false,
  [UebernahmeResult.CANCELLED]: true,
  [UebernahmeResult.ABANDONED]: true,
};

const pricedResultEndsOnDashboard = (result: UebernahmeResult | undefined): boolean =>
  result !== undefined && endsOnDashboard[result];

function* waitForUebernahme(
  last: TgvTakeOverOffer,
  loggedKeys: string[],
  userLeftBefore = false,
): SagaIterator<UebernahmeResult> {
  let deadline = Date.now() + UEBERNAHME_TIMEOUT_MS;
  let userLeft = userLeftBefore;
  let succeeded = false;
  let failed = false;
  while (true) {
    const event = yield race({
      success: take(GlobalActions.TARIFIERE_SHOP_SUCCESS),
      failure: take(GlobalActions.TARIFIERE_SHOP_FAILED),
      updateFailure: take(SHOP_UPDATE_FAILED_EVENTS),
      navigation: take(isNavigationToDashboard),
      retry: take(isTakeOverOf(last)),
      restoredRun: take(TgvActions.TGV_TAKE_OVER_AGAIN_REQUESTED),
      ...(succeeded || failed ? { settled: delay(0) } : {}),
      timeout: delay(Math.max(0, deadline - Date.now())),
    });
    if (event.updateFailure) {
      return UebernahmeResult.NOT_FINISHED;
    }
    if (event.restoredRun) {
      return UebernahmeResult.ABANDONED;
    }
    if (event.success) {
      succeeded = true;
    } else if (event.failure) {
      failed = true;
      succeeded = false;
    } else if (event.retry) {
      if (failed) {
        deadline = Date.now() + UEBERNAHME_TIMEOUT_MS;
      }
      failed = false;
    } else if (event.navigation) {
      if (succeeded) {
        return userLeft ? UebernahmeResult.CANCELLED : UebernahmeResult.DONE;
      }
      userLeft = true;
    } else if (event.settled) {
      if (failed) {
        return userLeft ? UebernahmeResult.FAILED_AFTER_LEAVING : UebernahmeResult.FAILED;
      }
      succeeded = false;
    } else {
      if (userLeft) {
        PS20Logger.info(
          'TGV_TAKE_OVER_ABANDONED',
          `The user went to the dashboard and the shop gave no answer for the take-over of ${loggedKeys.join(', ')}`,
          App.TGV,
        );
        return UebernahmeResult.ABANDONED;
      }
      throw new Error('The take-over of an offer into the dashboard did not finish');
    }
  }
}

const isDeletionOf =
  (keys: string[]) =>
  (action: AnyAction): boolean =>
    action.type === GlobalActions.PRODUKT_DELETED &&
    (action.payload?.angebotsIds ?? [])
      .map(childKeyOf)
      .some((key: string | null) => key !== null && keys.includes(key));

function* waitForDeletionRun(orphanKeys: string[]): SagaIterator<boolean> {
  let deadline = Date.now() + SHOP_RUN_WAIT_MS;
  let failed = false;
  while (true) {
    const event = yield race({
      success: take(GlobalActions.TARIFIERE_SHOP_SUCCESS),
      failure: take(GlobalActions.TARIFIERE_SHOP_FAILED),
      updateFailure: take(SHOP_UPDATE_FAILED_EVENTS),
      navigation: take(isNavigationToDashboard),
      retry: take(isDeletionOf(orphanKeys)),
      ...(failed ? { settled: delay(0) } : {}),
      timeout: delay(Math.max(0, deadline - Date.now())),
    });
    if (event.success) {
      return true;
    }
    if (event.failure) {
      failed = true;
    } else if (event.retry) {
      if (failed) {
        deadline = Date.now() + SHOP_RUN_WAIT_MS;
      }
      failed = false;
    } else {
      return false;
    }
  }
}

/**
 * The shop navigates to the dashboard once, after the take-over of the last offer. Only a
 * navigation that changes the address is reported by the browser; when the user is on the
 * dashboard already it reports nothing. The shop allows a navigation (`canNavigate`) until the
 * browser reports it, so the flag stays set, is saved with the Vorgang and makes the load show
 * "Browsernavigation nicht unterstützt". The missing report is added here.
 */
function* acknowledgeIdleNavigation(): SagaIterator {
  yield delay(NAVIGATION_SETTLE_MS);
  const shop = findStoreState(App.SHOP) as { canNavigate?: boolean } | undefined;
  if (shop?.canNavigate) {
    sharedDispatcher().dispatch(
      new GlobalActions().locationChanged({ appId: App.SHOP_DASHBOARD, subroute: '' }),
    );
  }
}

/**
 * The take-over of an offer that is not the last one is finished when the shop broadcast it: the
 * Warenkorb row of the offer is updated by then. The shop does nothing else for it.
 */
function* waitForDeferredUebernahme(produkt: TgvTakeOverOffer): SagaIterator {
  const { done } = yield race({
    done: take(
      (action: AnyAction) =>
        action.type === GlobalActions.IN_DASHBOARD_UEBERNEHMEN &&
        action.payload?.appId === produkt.app &&
        action.payload?.angebotsId === produkt.angebotsId,
    ),
    timeout: delay(UEBERNAHME_TIMEOUT_MS),
  });
  if (!done) {
    throw new Error('The take-over of an offer into the dashboard did not finish');
  }
  // the broadcast to the other stores may still be running; the next offer starts after it
  yield delay(0);
}

function* takeRestoredOffersOverAgain(
  restoredKeys: string[],
  offers: TgvTakeOverOffer[],
  priceLast = false,
  userLeft = false,
): SagaIterator<UebernahmeResult | undefined> {
  const restored = offers.filter(offer =>
    restoredKeys.includes(childKeyOf({ appId: offer.app, angebotsId: offer.angebotsId })),
  );
  const loggedKeys = restored.map(restoredOffer => restoredOffer.key);
  let pricedResult: UebernahmeResult | undefined;
  for (const [index, offer] of restored.entries()) {
    const priced = priceLast && index === restored.length - 1;
    const waiting = yield priced
      ? fork(waitForUebernahme, offer, loggedKeys, userLeft)
      : fork(waitForDeferredUebernahme, offer);
    const type = INS_DASHBOARD_ACTION[offer.app];
    const sent = dispatchToStoreOf(
      offer.app,
      offer.angebotsId,
      priced ? { type } : { type, payload: { deferTarifierung: true } },
    );
    if (sent) {
      const result: UebernahmeResult | undefined = yield join(waiting);
      pricedResult = result;
    } else {
      yield cancel(waiting);
    }
  }
  return pricedResult;
}

export function* takeRequestedOffersOverAgain(
  action: TgvAction<TakeOverAgainPayload>,
): SagaIterator {
  const appState: IAppState = yield select();
  const { keys, priceLast } = action.payload;
  const pricedResult = yield* takeRestoredOffersOverAgain(
    keys,
    readTakeOverOffers(appState),
    priceLast,
    true,
  );
  if (pricedResultEndsOnDashboard(pricedResult)) {
    yield call(acknowledgeIdleNavigation);
  }
}

function* abortFailedTakeOver(
  tiergruppe: TiergruppeState,
  offers: TgvTakeOverOffer[],
  offersDeletedBeforeTakeOver: boolean,
  userLeft: boolean,
): SagaIterator {
  let pricedResult: UebernahmeResult | undefined;
  try {
    const restoredKeys = restoreRepeatedRun(tiergruppe);
    pricedResult = yield* takeRestoredOffersOverAgain(
      restoredKeys,
      offers,
      offersDeletedBeforeTakeOver && !hasAbortedOffers(tiergruppe),
      userLeft,
    );
  } finally {
    snapshotManager.markTakeOversSettled();
    const shopNavigated = userLeft || pricedResultEndsOnDashboard(pricedResult);
    removeAbortedOffers(!shopNavigated, tiergruppe);
  }
  if (userLeft || pricedResult !== UebernahmeResult.ABANDONED) {
    yield call(acknowledgeIdleNavigation);
  }
}

/**
 * Every child offer takes itself over into the dashboard, so each one stays an own application
 * like it would after its own offer page. The group page only triggers them, one after the other.
 * The shop looks at the whole Warenkorb for its requests (sofortschutz, zaehlstuecke, and the
 * Shop-Tarifierung of every offer in it), so that is done once, with the last offer: the others
 * are marked with `deferTarifierung` and only enter the Warenkorb.
 */
export function* insDashboardSaga(): SagaIterator {
  const appState: IAppState = yield select();
  const produkte = readTakeOverOffers(appState);
  if (!areAllLinienPriced(produkte)) {
    return;
  }
  try {
    yield put(new TgvActions().takeOverStarted());
    sharedDispatcher().dispatch(new GlobalActions().tiergruppeTakeOverStarted());
    yield call(takeOffersOver, appState, produkte);
  } finally {
    try {
      yield put(new TgvActions().takeOverEnded());
    } finally {
      sharedDispatcher().dispatch(new GlobalActions().tiergruppeTakeOverEnded());
    }
  }
}

function* takeOffersOver(appState: IAppState, produkte: TgvTakeOverOffer[]): SagaIterator {
  const orphanKeys = findOrphanKeys(appState);
  const offersDeleted = orphanKeys.length > 0;
  if (offersDeleted) {
    const deletionRun = yield fork(waitForDeletionRun, orphanKeys);
    yield call(removeOrphanedOffersSaga);
    const deletionSucceeded: boolean = yield join(deletionRun);
    if (!deletionSucceeded) {
      return;
    }
    const afterDeletion: IAppState = yield select();
    produkte = readTakeOverOffers(afterDeletion);
    if (!areAllLinienPriced(produkte)) {
      return;
    }
  }
  const loggedKeys = produkte.map(produkt => produkt.key);
  for (const [index, produkt] of produkte.entries()) {
    const isLast = index === produkte.length - 1;
    // listen before sending, so that a fast answer cannot be missed
    const waiting = yield isLast
      ? fork(waitForUebernahme, produkt, loggedKeys)
      : fork(waitForDeferredUebernahme, produkt);
    const type = INS_DASHBOARD_ACTION[produkt.app];
    if (!type) {
      throw new Error(`No take-over action known for ${produkt.app}`);
    }
    const sent = dispatchToStoreOf(
      produkt.app,
      produkt.angebotsId,
      isLast ? { type } : { type, payload: { deferTarifierung: true } },
    );
    if (!sent) {
      throw new Error(`Store of ${produkt.app} ${produkt.angebotsId ?? ''} does not exist`);
    }
    snapshotManager.markTakeOverSent(
      childKeyOf({ appId: produkt.app, angebotsId: produkt.angebotsId }),
    );
    const result: UebernahmeResult | undefined = yield join(waiting);
    if (!isLast) {
      continue;
    }
    switch (result) {
      case UebernahmeResult.FAILED:
      case UebernahmeResult.FAILED_AFTER_LEAVING: {
        const current: IAppState = yield select();
        yield call(
          abortFailedTakeOver,
          current.tiergruppe,
          produkte,
          offersDeleted,
          result === UebernahmeResult.FAILED_AFTER_LEAVING,
        );
        return;
      }
      case UebernahmeResult.CANCELLED:
        yield call(acknowledgeIdleNavigation);
        return;
      case UebernahmeResult.NOT_FINISHED:
      case UebernahmeResult.ABANDONED:
        return;
      case UebernahmeResult.DONE:
        break;
      default: {
        const unhandled: never = result;
        throw new Error(`Unhandled take-over result ${String(unhandled)}`);
      }
    }
  }
  snapshotManager.markTakeOversSettled();
  // only now, so that a failure within this run still removes everything of it
  yield put(new TgvActions().uebernommen(produkte.map(produkt => produkt.uebernommenKey)));
  sharedDispatcher().dispatch(new GlobalActions().tiergruppeUebernommen());
  yield call(acknowledgeIdleNavigation);
}
